import {
  Activity, Apple, BookOpen, Briefcase, Building2, Calculator, Car, CarFront, ChefHat, Code, Coffee, Cog,
  Cpu, Croissant, Dumbbell, Factory, Flower2, Gem, GraduationCap, Heart, HeartPulse, Hospital, Hotel,
  KeyRound, Lamp, Layers, Leaf, Lightbulb, Megaphone, MonitorPlay, Package, PartyPopper, Pill, Plane,
  Presentation, RadioTower, Refrigerator, Scale, Scissors, Server, Shapes, Shirt, ShoppingBag, ShoppingCart,
  Smartphone, Smile, Sofa, Sparkles, Stethoscope, Store, Tag, Truck, Tv, UtensilsCrossed, Wifi, Wrench,
  type LucideIcon,
} from 'lucide-react';
import { INDUSTRY_OPTIONS, type IndustryValue } from '@/services/business-profile.service';

// Same icon per industry as super-admin/src/pages/industry-configs/industry-meta.ts,
// so an industry reads the same in both consoles.
const INDUSTRY_ICONS: Record<string, LucideIcon> = {
  restaurant: UtensilsCrossed, cafe: Coffee, bakery: Croissant, catering: ChefHat,
  software: Code, it_services: Server, agency: Layers, digital_marketing: Megaphone,
  mobile_shop: Smartphone, mobile_repair: Wrench, electronics: Tv, appliances: Refrigerator,
  furniture: Sofa, home_decor: Lamp, fashion: Shirt,
  clothing_brand: Tag, cosmetics_brand: Gem, manufacturer: Factory,
  beauty_salon: Sparkles, spa: Flower2, barber_shop: Scissors,
  healthcare: HeartPulse, clinic: Stethoscope, hospital: Hospital, dental: Smile, pharmacy: Pill,
  real_estate: Building2, property_rental: KeyRound,
  education: GraduationCap, tutoring: BookOpen, coaching: Presentation, online_course: MonitorPlay,
  fitness: Activity, gym: Dumbbell, yoga_studio: Leaf,
  travel_agency: Plane, hotel: Hotel, car_rental: CarFront,
  ecommerce: ShoppingCart, retail: Store, grocery: Apple,
  professional_services: Briefcase, legal: Scale, accounting: Calculator, consulting: Lightbulb,
  automotive: Cog, car_dealership: Car,
  event_planning: PartyPopper, wedding: Heart,
  courier: Package, logistics: Truck,
  telecom: RadioTower, internet_provider: Wifi,
  other: Shapes,
};

const GROUP_ICONS: Record<string, LucideIcon> = {
  'Food & Hospitality': UtensilsCrossed,
  Technology: Cpu,
  'Retail & Electronics': Smartphone,
  'Brands & Manufacturing': Factory,
  'Beauty & Wellness': Sparkles,
  'Health & Medical': Stethoscope,
  'Real Estate': Building2,
  Education: GraduationCap,
  'Fitness & Sport': Dumbbell,
  'Travel & Hospitality': Plane,
  Commerce: ShoppingBag,
  Professional: Briefcase,
  Automotive: Car,
  Events: PartyPopper,
  'Logistics & Delivery': Truck,
  Communications: RadioTower,
  Other: Shapes,
};

export type IndustryOption = (typeof INDUSTRY_OPTIONS)[number];

export const industryOption = (value?: string | null): IndustryOption | undefined =>
  INDUSTRY_OPTIONS.find((o) => o.value === value);

export const industryIcon = (value?: string | null): LucideIcon => {
  const opt = industryOption(value);
  return (value && INDUSTRY_ICONS[value]) || (opt && GROUP_ICONS[opt.group]) || Shapes;
};

export const groupIcon = (group: string): LucideIcon => GROUP_ICONS[group] ?? Shapes;

export const isIndustryValue = (v: unknown): v is IndustryValue =>
  typeof v === 'string' && INDUSTRY_OPTIONS.some((o) => o.value === v);

/** Options grouped in catalogue order, for pickers. */
export function groupedIndustryOptions(options: readonly IndustryOption[] = INDUSTRY_OPTIONS) {
  const groups: { group: string; options: IndustryOption[] }[] = [];
  for (const opt of options) {
    let g = groups.find((x) => x.group === opt.group);
    if (!g) { g = { group: opt.group, options: [] }; groups.push(g); }
    g.options.push(opt);
  }
  return groups;
}
